import React from 'react';
import type { Page, NavParams, CustomIndustry } from '@types';
import { SUITE_DETAILS, PILLAR_SUITES, SUITE_IMAGES } from './constants';

interface DashboardProps {
    industry: string;
    db: any;        // Add this
    isLoaded: boolean; // Add this
    onNavigateToGtgDashboard: () => void;
    onNavigateToGsocDashboard: () => void;
    onRequestHelp: (suiteName: string, workflowName?: string) => void;
    customIndustries: CustomIndustry[];
    onNavigateToSuite: (suiteName: string) => void;
    onOpenAvToolsModal: () => void;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
}

interface SuiteCardProps {
    suite: {
        name: string;
        description: string;
        icon: React.ReactElement;
        color: { bg: string; text: string; };
        comingSoon?: boolean;
    };
    imageUrl?: string;
    onClick: () => void;
}

const SuiteCard: React.FC<SuiteCardProps> = ({ suite, imageUrl, onClick }) => (
    <div
        onClick={onClick}
        className="relative group cursor-pointer bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col text-left overflow-hidden"
    >
        {imageUrl && (
             <img src={imageUrl} alt="" className="absolute top-0 right-0 h-20 w-28 object-cover rounded-bl-full opacity-50 group-hover:opacity-80 transition-opacity duration-300" aria-hidden="true" />
        )}
        <div className="relative z-10">
            <div className={`p-3 rounded-lg ${suite.color.bg} ${suite.color.text} self-start mb-4 inline-block`}>
                {React.cloneElement(suite.icon as React.ReactElement<any>, { className: 'h-7 w-7' })}
            </div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {suite.name}
                {suite.comingSoon && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 rounded-full border border-purple-200 animate-pulse">
                        Coming Soon
                    </span>
                )}
            </h3>
            <p className="mt-2 text-sm text-slate-600 flex-grow">
                {suite.comingSoon ? "New Feature. coming soon !" : suite.description}
            </p>
        </div>
        <div className="relative z-10 text-right mt-auto pt-4 h-5">
            {!suite.comingSoon && (
                <span className="text-sm font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Select Suite &rarr;
                </span>
            )}
        </div>
    </div>
);

const threatGroupSuite = {
    name: "Threat Group OSINT",
    description: "Investigate state-sponsored and non-state threat actors globally or by country.",
    icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>,
    color: { bg: 'bg-red-100', text: 'text-red-600' }
};

export const Dashboard: React.FC<DashboardProps> = ({ industry, onNavigateToGtgDashboard, onNavigateToGsocDashboard, customIndustries, onNavigateToSuite, onNavigate }) => {

    let availableSuiteKeys: string[] = PILLAR_SUITES[industry] || [];
    const customIndustry = customIndustries.find(ci => ci.name === industry);
    if (customIndustry) {
        availableSuiteKeys = customIndustry.suites;
    }

    return (
        <div className="animate-fade-in space-y-8">

            {/* 2. Strategic Integration Action Card */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white shadow-lg flex flex-col md:flex-row items-center justify-between gap-6 border border-slate-700">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-600 rounded-lg shadow-lg shadow-blue-900/50 hidden md:block">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z" /></svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white">Strategic Framework Builder</h3>
                        <p className="text-slate-300 text-sm mt-1 leading-relaxed">
                            Need a comprehensive security strategy? The <strong>SRM Auto-Architect</strong> can build a full framework tailored to {industry}.
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => onNavigate({ page: 'srm_command_center', params: { view: 'architect' } })}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 px-6 rounded-lg shadow-lg transition-transform transform active:scale-95 whitespace-nowrap text-sm flex items-center gap-2"
                >
                    Launch Architect
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </button>
            </div>

            <div>
                <h2 className="text-3xl font-bold text-slate-800">{industry} Pillar Tools</h2>
                <p className="text-slate-500 mb-6">Select from your curated list of intelligence suites within this pillar to begin analysis.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {availableSuiteKeys.map(suiteKey => {
                        const suite = SUITE_DETAILS[suiteKey];
                        if (!suite) return null;
                        const imageUrl = SUITE_IMAGES[suiteKey];

                        if (suiteKey === 'gsoc') {
                            return <SuiteCard key={suiteKey} suite={suite} onClick={onNavigateToGsocDashboard} imageUrl={imageUrl} />;
                        }
                        if (suite.comingSoon) {
                            return <SuiteCard key={suiteKey} suite={suite} onClick={() => {}} imageUrl={imageUrl}/>;
                        }
                        return <SuiteCard key={suiteKey} suite={suite} onClick={() => onNavigate({ page: 'suiteDashboard', params: { suiteName: suite.name }})} imageUrl={imageUrl}/>;
                    })}
                    {industry === 'GeoPolitical Risk Management and Intelligence' && (
                        <SuiteCard 
                            suite={threatGroupSuite} 
                            onClick={onNavigateToGtgDashboard} 
                            imageUrl={SUITE_IMAGES.gtg} 
                        />
                    )}
                </div>
            </div>
        </div>
    );
};